import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import ScrollFadeIn from "../components/ScrollFadeIn";
import SEO from "../components/SEO";
import structuredData from "../data/structuredData.json";
import Pagination from "../components/Pagination";
import { Link } from "react-router-dom";
import { IconChevronRight, IconCalendar, IconClock } from "@tabler/icons-react";

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
  });

  // Get current filters from URL
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const selectedCategory = searchParams.get("category") || "";

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch("/api/categories");
        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    fetchCategories();
  }, []);

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/posts?page=${currentPage}&per_page=9`;
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch posts");
      }
      const data = await response.json();
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCategoryChange = (categoryId) => {
    const newParams = new URLSearchParams();
    if (categoryId) {
      newParams.set("category", categoryId);
    }
    // Reset to page 1 when changing category
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]+>/g, "");
    const wordCount = text.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime;
  };

  // Decode HTML entities like &amp; to &
  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  return (
    <>
      <SEO
        title="Engineering Insights & Industrial Solutions Blog | GXL Engineering"
        description="Explore engineering insights, precision manufacturing, industrial technology, and practical solutions from GXL Engineering experts."
        url="/blog"
        type="website"
        noSuffix
        structuredData={structuredData.blog}
      />
      <div className="bg-[#0B0F11]">
        <Header />
      </div>

      {/* Hero Section */}
      <section className="bg-[#0B0F11] lg:pt-20 pt-10 lg:pb-28 pb-16">
        <div className="w-full max-w-[1600px] mx-auto lg:px-10 px-6">
          <ScrollFadeIn>
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block text-[#fcc171] text-sm font-medium tracking-wider uppercase mb-4">
                Insights & Resources
              </span>
              <h1 className="text-white lg:text-5xl text-3xl font-semibold mb-4">
                Engineering Insights
              </h1>
              <p className="text-[rgb(171,174,187)] text-lg">
                Explore our latest articles on precision engineering,
                manufacturing technology, and industry best practices.
              </p>
            </div>
          </ScrollFadeIn>

          {/* Category Filter */}
          {categories.length > 0 && (
            <ScrollFadeIn delay={0.1}>
              <div className="flex flex-wrap justify-center gap-3 mt-10">
                <button
                  onClick={() => handleCategoryChange("")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    !selectedCategory
                      ? "bg-[#fcc171] text-[#0B0F11]"
                      : "bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]"
                  }`}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id.toString())}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      selectedCategory === category.id.toString()
                        ? "bg-[#fcc171] text-[#0B0F11]"
                        : "bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]"
                    }`}
                  >
                    {decodeHtml(category.name)}
                  </button>
                ))}
              </div>
            </ScrollFadeIn>
          )}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="bg-[#E5E5E6] lg:py-20 py-10">
        <div className="w-full max-w-[1600px] mx-auto lg:px-10 px-6">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="loader">
                <div className="bar"></div>
                <div className="bar"></div>
                <div className="bar"></div>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">{error}</p>
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-8">
                {posts.map((post, index) => (
                  <ScrollFadeIn key={post.id} delay={index * 0.1}>
                    <Link
                      to={`/blog/${post.slug}`}
                      reloadDocument
                      className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <div className="overflow-hidden h-[220px] bg-gray-200">
                        <img
                          src={
                            post.featuredImage ||
                            "https://placehold.co/600x400/e5e5e6/9ca3af?text=No+Image"
                          }
                          alt={post.title.rendered.replace(/<[^>]+>/g, "")}
                          className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-gray-500 text-sm mb-3">
                          <span className="flex items-center gap-1">
                            <IconCalendar size={16} stroke={1.5} />
                            {formatDate(post.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <IconClock size={16} stroke={1.5} />
                            {getReadingTime(post.content.rendered)} min read
                          </span>
                        </div>
                        <h2
                          className="text-gray-900 text-xl font-semibold mb-3 line-clamp-2 group-hover:text-[#C17C56] transition-colors duration-300"
                          dangerouslySetInnerHTML={{
                            __html: post.title.rendered,
                          }}
                        />
                        <div
                          className="text-gray-600 text-base line-clamp-3 mb-4"
                          dangerouslySetInnerHTML={{
                            __html: post.excerpt.rendered,
                          }}
                        />
                        <span className="inline-flex items-center text-[#C17C56] font-medium group-hover:gap-2 transition-all duration-300">
                          Read More
                          <IconChevronRight
                            size={18}
                            stroke={2}
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          />
                        </span>
                      </div>
                    </Link>
                  </ScrollFadeIn>
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">
                {selectedCategory
                  ? "No posts found in this category."
                  : "No posts available yet."}
              </p>
              {selectedCategory && (
                <button
                  onClick={() => handleCategoryChange("")}
                  className="mt-4 text-[#C17C56] font-medium hover:underline"
                >
                  View all posts
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B0F11] lg:py-20 py-10">
        <div className="w-full max-w-[1600px] mx-auto lg:px-10 px-6">
          <ScrollFadeIn>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-white lg:text-3xl text-2xl font-semibold mb-4">
                Have questions about precision engineering?
              </h2>
              <p className="text-[rgb(171,174,187)] mb-8">
                Our team of experts is ready to help you find the right
                solutions for your manufacturing needs.
              </p>
              <a
                href="/contact"
                className="transition-colors duration-300 group inline-flex items-center outline-none justify-center tracking-tight leading-none focus:outline-white focus:outline-1 focus:outline-offset-4 h-12 text-[15px] px-8 rounded-full shadow-[0px_2px_2px_0px_rgba(0,0,0,0.74)] font-semibold relative"
              >
                <span className="absolute -inset-px bottom-[-1.5px] rounded-full bg-[linear-gradient(180deg,#fcc171_0%,#C17C56_50%,#362821_100%)]"></span>
                <span className="absolute -top-[5px] bottom-0.5 left-1/2 w-[91%] -translate-x-1/2 bg-btn-glowing mix-blend-screen blur-[1px] transition-transform duration-300 ease-in-out group-hover:translate-y-[-2px]"></span>
                <span className="absolute inset-0 rounded-full bg-black"></span>
                <span className="absolute inset-0 rounded-full bg-btn-glowing-inset opacity-100 transition-opacity duration-300 ease-in-out group-hover:opacity-0"></span>
                <span className="absolute inset-0 rounded-full bg-btn-glowing-inset-hover opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></span>
                <span className="relative z-20 bg-[linear-gradient(180deg,#FFF_33.33%,#E4D0B1_116.67%)] bg-clip-text text-transparent">
                  Contact Us
                </span>
              </a>
            </div>
          </ScrollFadeIn>
        </div>
      </section>
    </>
  );
};

export default Blog;
